import React, { useMemo, useState } from 'react';
import { DocumentIcon, FolderIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { NoResultsChat } from './NoResultsChat';
import { SearchFilters } from './SearchFilters';

interface SearchResult {
  id: string;
  title: string;
  type: 'document' | 'folder';
  path: string;
  lastModified: string;
  size?: string;
  tags?: string[];
  author?: string;
  category?: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
  onResultClick?: (result: SearchResult) => void;
  query?: string;
}

export const SearchResults: React.FC<SearchResultsProps> = ({ results, isLoading, onResultClick, query }) => {
  const [filters, setFilters] = useState({
    category: 'Todas as categorias',
    type: 'Todos os tipos',
    date: 'Qualquer data',
    author: 'Todos os autores',
  });

  // Extrair valores únicos para os filtros a partir dos resultados
  const categories = useMemo(() => {
    const set = new Set<string>();
    results.forEach(r => r.category && set.add(r.category));
    return Array.from(set);
  }, [results]);

  const types = useMemo(() => {
    const set = new Set<string>();
    results.forEach(r => set.add(r.type === 'document' ? 'Documento' : 'Pasta'));
    return Array.from(set);
  }, [results]);

  const dates = useMemo(() => {
    const set = new Set<string>();
    results.forEach(r => {
      if (r.lastModified) {
        const year = new Date(r.lastModified).getFullYear();
        set.add(year.toString());
      }
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [results]);

  const authors = useMemo(() => {
    const set = new Set<string>();
    results.forEach(r => r.author && set.add(r.author));
    return Array.from(set);
  }, [results]);

  // Filtragem local
  const filteredResults = useMemo(() => {
    return results.filter(r => {
      const matchCategory = filters.category === 'Todas as categorias' || r.category === filters.category;
      const matchType = filters.type === 'Todos os tipos' || (filters.type === 'Documento' && r.type === 'document') || (filters.type === 'Pasta' && r.type === 'folder');
      const matchDate = filters.date === 'Qualquer data' || (r.lastModified && new Date(r.lastModified).getFullYear().toString() === filters.date);
      const matchAuthor = filters.author === 'Todos os autores' || r.author === filters.author;
      return matchCategory && matchType && matchDate && matchAuthor;
    });
  }, [results, filters]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (results.length === 0) {
    return <NoResultsChat query={query || ''} />;
  }

  return (
    <div>
      {results.length > 10 && (
        <SearchFilters
          categories={categories}
          types={types}
          dates={dates}
          authors={authors}
          onApply={setFilters}
        />
      )}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredResults.map((result) => (
            <li key={result.id}>
              <Link
                to={result.path}
                onClick={() => onResultClick?.(result)}
                className="block hover:bg-gray-50"
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center">
                    {result.type === 'document' ? (
                      <DocumentIcon className="h-6 w-6 text-gray-400 mr-3" />
                    ) : (
                      <FolderIcon className="h-6 w-6 text-gray-400 mr-3" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-600 truncate">
                        {result.title}
                      </p>
                      <div className="mt-2 flex">
                        <div className="flex items-center text-sm text-gray-500">
                          <p>
                            Última modificação: {new Date(result.lastModified).toLocaleDateString()}
                          </p>
                          {result.size && (
                            <>
                              <span className="mx-2">•</span>
                              <p>{result.size}</p>
                            </>
                          )}
                        </div>
                      </div>
                      {result.tags && result.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {result.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}; 